/**
 * `store-name` - the shop's name without a model: the best of the header
 * lines, the footer's web address and the words that repeat
 * (`merchantNameCandidates`), tidied the way a stored name is (`storeName`):
 * no legal form, spelt as the registry spells it when a `company` step
 * before it found one, else as another header line spells it, else out of
 * all capitals.
 *
 * A logo is what OCR reads worst, and the same name printed in plain text
 * further down is what it reads best. So the first guess is checked against
 * the plain-text spellings of a name on the receipt - the footer's web
 * address, a line with a legal form - before it is offered:
 *
 * - a header line that spells the web address exactly is the name, ahead of
 *   a first line that does not (`OYNIVILO` above `BAUHAUS`, `www.BAUHAUS.se`);
 * - a header word a letter or two off one of them is that name misread, and
 *   the plain spelling is offered instead (`PODER` and `www.power.se`,
 *   `TOSTCO` and `Costco Wholesale Sweden AB`).
 *
 * Needs `text`. Provides `candidates:merchant`, a `{ name }` each, best first
 * with its confidence. No options.
 */

import { foldForMatching, isLegalToken, similarity } from '../../fuzzy.js';
import { merchantNameCandidates, webAddressNames, type NameCandidate } from '../../merchant-name.js';
import { storeName } from '../../store-name.js';
import type { Candidate, Step } from '../types.js';
import { summarize } from './support.js';

/** How many names are offered: enough for a trace to show the runner-up. */
const OFFERED = 3;

/**
 * Whether a guess reads as a word rather than what OCR does to a logo:
 * `AO0UHANE` has a digit standing in for a letter, and no Swedish name has
 * five consonants in a row. Short names with many vowels (`IKEA`) and odd
 * case (`WiLLY:S`) are printed names, so `looksSearchable`'s stricter test,
 * made to spare a search quota, is not the one used here.
 */
function readable(guess: NameCandidate): boolean {
  return !/\p{L}\d|\d\p{L}/u.test(guess.query) && !/[^aeiouyåäöéü\s\P{L}]{5,}/iu.test(guess.query);
}

/** How many of the leading lines a printed name is looked for in. Matches `merchantNameCandidates`. */
const HEAD_LINES = 14;

/** A guess's words, folded, without legal forms: `Costco Wholesale Sweden AB.` → costco, wholesale. */
function tokens(text: string): string[] {
  return foldForMatching(text)
    .split(' ')
    .filter((token) => token.length > 1 && !isLegalToken(token));
}

/**
 * Whether a guess OCR made of a logo is printed again in the header, which
 * noise is not: `OKQ8` in `OKQ8A` and `OKQ8 FARSTA` is the name, digit and all.
 */
function repeated(guess: NameCandidate, head: readonly string[]): boolean {
  return tokens(guess.query).some((token) => token.length >= 3 && head.filter((line) => line.includes(token)).length >= 2);
}

/** Whether a guess has the name among its words, or is it with a space in it (`WiLLY:S`). */
function spells(guess: NameCandidate, name: string): boolean {
  return tokens(guess.query).includes(name) || foldForMatching(guess.query).replace(/ /g, '') === name;
}

/** The lines a logo is printed in, from the top. */
const LOGO_LINES = 3;

/** Similarity at which a header word is taken to be a plain-text name misread. */
const MISREAD = 0.75;

/** Looser, for a second misreading of the same logo: `COSTa` beside `TOSTCO`. */
const ALSO_MISREAD = 0.6;

/**
 * The guesses in the order to offer them, after checking the first against
 * the plain-text spellings of a name: see the step's description.
 *
 * A plain-text spelling is read by OCR too, and is sometimes the one that is
 * wrong (`www.BAUHAIJS.se` under a clean `BAUHAUS`). So it replaces the header
 * only when the header cannot agree with itself: two different near-spellings
 * of it (`PODER` and `Pouer`), which a logo misread gives and a clean one
 * does not.
 */
function corroborated(found: NameCandidate[], text: string): NameCandidate[] {
  const top = found[0];
  if (!top) return found;
  const header = found.filter((guess) => guess.source === 'header');
  const anchors = [
    ...webAddressNames(text).map((guess) => ({ guess, domain: true })),
    ...header.filter((guess) => /\b(AB|Aktiebolag)\b/.test(guess.text)).map((guess) => ({ guess, domain: false })),
  ];
  for (const { guess: anchor, domain } of anchors) {
    const name = tokens(anchor.query)[0];
    if (!name || name.length < 3) continue;
    if (spells(top, name)) return found;
    const others = header.filter((guess) => guess !== anchor);
    const confirmed = domain ? others.find((guess) => spells(guess, name)) : undefined;
    if (confirmed) return [confirmed, ...found.filter((guess) => guess !== confirmed)];
    // A legal name is often the franchisee's (`Farsta Livs AB`), so only a
    // logo - a single word among the first lines - is taken as a misreading of it.
    const logos = domain ? others : others.filter((guess) => guess.line < LOGO_LINES && tokens(guess.query).length === 1);
    const near = new Map<string, number>();
    for (const token of logos.flatMap((guess) => tokens(guess.query))) {
      if (token !== name && token.length > 3) near.set(token, similarity(token, name));
    }
    const scores = [...near.values()].filter((score) => score >= ALSO_MISREAD);
    if (scores.length >= 2 && scores.some((score) => score >= MISREAD)) {
      return [anchor, ...found.filter((guess) => guess !== anchor)];
    }
  }
  return found;
}

/** A web address's label as a name: `bauhaus` → `Bauhaus`. */
const capitalised = (label: string): string => label.charAt(0).toLocaleUpperCase('sv') + label.slice(1);

export const storeNameStep: Step<Record<string, never>> = {
  name: 'store-name',
  needs: () => ['text'],
  provides: () => ['candidates:merchant'],
  async run(state, context) {
    const company = state.company ? { name: state.company.name } : null;
    const text = state.input.text ?? '';
    const head = text
      .split(/\r?\n/)
      .map((line) => foldForMatching(line))
      .filter(Boolean)
      .slice(0, HEAD_LINES);
    // Noise last, whatever its position: a mangled logo at the top loses to the footer's web address.
    const all = merchantNameCandidates(text);
    const legible = (guess: NameCandidate) => readable(guess) || repeated(guess, head);
    const found = corroborated([...all.filter(legible), ...all.filter((guess) => !legible(guess))], text);
    const header = found.filter((candidate) => candidate.source === 'header').map((candidate) => candidate.text);
    const candidates: Candidate[] = [];
    for (const guess of found) {
      // A web address is offered as its label: `hemkop` from `www.hemkop.se`.
      const others = header.filter((line) => line !== guess.text);
      const name = storeName(guess.source === 'domain' ? capitalised(guess.query) : guess.text, company, others);
      if (!name || candidates.some((candidate) => (candidate.value as { name: string }).name === name)) continue;
      candidates.push({ value: { name }, source: context.id, confidence: guess.confidence, evidence: [guess.line + 1] });
      if (candidates.length === OFFERED) break;
    }
    if (candidates.length === 0) return { skipped: 'no name in the header' };
    return { candidates: { merchant: candidates }, added: summarize({ merchant: candidates }) };
  },
};
